import { IProductConfigOption } from "@montana/models/dev";

/**
 * Helper function for setting desired pixel size on CAPI url
 * @param imgUrl url to manipulate for proper URL
 * @param number desired pixel size of image.
 * @param ratio optional ratio which is used to determine padding provided by CAPI
 */
export function transformCylindoImageSize(
    imgUrl: string,
    width: number,
    ratio?: number
) {
    const apiVersion2 = imgUrl.includes("/v2/");
    const hasSize = /(size=)[^&]+/g.test(imgUrl);
    const sizeString = apiVersion2
        ? `size=${!!ratio ? `(${width},${Math.round(ratio * width)})` : width}`
        : `size=${width}`;
    if (hasSize) {
        return imgUrl.replace(/(size=)[^&]+/g, sizeString);
    } else {
        return /\?.+=.*/.test(imgUrl)
            ? `${imgUrl}&${sizeString}`
            : `${imgUrl}?${sizeString}`;
    }
}

/**
 * Helper function for setting desired frame on cylindo image url
 * @param imgUrl url to manipulate for proper URL
 * @param frame number value of desire frame (Must be between 1-32) 1 = 0 deg,
 */
export function transformCylindoImageFrame(imgUrl: string, frame: number) {
    const apiVersion2 = imgUrl.includes("/v2/");

    if (apiVersion2) {
        const hasFrame = /(frames\/)[^/]+/g.test(imgUrl);
        if (hasFrame) {
            return imgUrl.replace(/(frames\/)[^/]+/g, `frames/${frame}`);
        } else {
            const splitUrl = imgUrl.match(/([^?]+)(\?.*)?/g);
            return splitUrl && splitUrl[0]
                ? `${splitUrl}/frames/${frame}${splitUrl[1] || ""}`
                : `${imgUrl}`;
        }
    } else {
        const hasFrame = /(frame=)[^&]+/g.test(imgUrl);
        if (hasFrame) {
            return imgUrl.replace(/(frame=)[^&]+/g, `frame=${frame}`);
        } else {
            return /\?.+=.*/.test(imgUrl)
                ? `${imgUrl}&frame=${frame}`
                : `${imgUrl}?frame=${frame}`;
        }
    }
}

/**
 * Helper function for setting desired background color on cylindo image url
 * @param imgUrl url to manipulate for proper URL
 * @param backgroundColor hex value of desired color without hashtag --> example: 'FAFAFA'
 */
export function transformCylindoImageBackground(
    imgUrl: string,
    backgroundColor: string
) {
    const apiVersion2 = imgUrl.includes("/v2/");

    const regex = apiVersion2 ? /(background=)[^&]+/g : /(bgcolor=)[^&]+/g;
    const param = apiVersion2 ? "background=" : "bgcolor=";

    const hasBackground = regex.test(imgUrl);

    // Clean # from backgroundColor if hex value
    const cleanBackgroundHex = backgroundColor.replace("#", "");

    if (hasBackground) {
        return imgUrl.replace(regex, `${param + cleanBackgroundHex}`);
    } else {
        return /\?.+=.*/.test(imgUrl)
            ? `${imgUrl}&${param + cleanBackgroundHex}`
            : `${imgUrl}?${param + cleanBackgroundHex}`;
    }
}

/**
 * Helper function for settings desired format on cylindo image url
 * @param imgUrl url to manipulate for proper URL
 * @param format string value of desired format (Must be 'jpg' or 'png')
 */
export function transformCylindoImageFormat(
    imgUrl: string,
    format: "jpg" | "png"
) {
    const hasFormat = /(format=)[^&]+/g.test(imgUrl);
    if (hasFormat) {
        return imgUrl.replace(/(format=)[^&]+/g, `format=${format}`);
    } else {
        return /\?.+=.*/.test(imgUrl)
            ? `${imgUrl}&format=${format}`
            : `${imgUrl}?format=${format}`;
    }
}

export function transformFeatures(
    dimensions: IProductConfigOption[]
): string[] {
    return dimensions
        .filter(
            (dimension: IProductConfigOption) =>
                dimension.cylindoFeatureParameters
        )
        .map((dimension: IProductConfigOption) => {
            if (dimension.cylindoFeatureParameters) {
                // replace 'uden ophæng' because cylindo does not understand the lovely danish language or this option whatsoever,
                // also WALL yields the same image so all is well. Long live the PIM and cylindo symbiosis.
                const features = dimension.cylindoFeatureParameters
                    .replace(/uden oph[æ|ae|a|e]ng/gi, "WALL")
                    .split("&");
                if (features.length > 1) {
                    return features
                        .map(feature => {
                            return feature.split("=")[1].split(":");
                        })
                        .join(",")
                        .split(",");
                } else {
                    if (features[0].startsWith("feature=")) {
                        features[0] = features[0].replace("feature=", "");
                    }
                    return features[0].split(":");
                }
            }
        })
        .join(",")
        .split(",");
}
